import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dot"]

  declare readonly dotTargets: HTMLAnchorElement[]

  private observer: IntersectionObserver | null = null
  private visibleSections: Set<string> = new Set()
  private onScroll = () => this.updateActive()

  connect() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id
          if (entry.isIntersecting) {
            this.visibleSections.add(id)
          } else {
            this.visibleSections.delete(id)
          }
        })
        this.updateActive()
      },
      { rootMargin: "-20% 0px -60% 0px" }
    )

    this.dotTargets.forEach((dot) => {
      const section = document.getElementById(dot.dataset.section || "")
      if (section) this.observer!.observe(section)
    })

    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("scroll", this.onScroll)
  }

  private isScrolledToBottom(): boolean {
    return (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 50)
  }

  private updateActive() {
    const orderedIds = this.dotTargets.map((d) => d.dataset.section || "")

    if (this.isScrolledToBottom()) {
      // At the bottom of the page, activate the last section
      this.setActive(orderedIds[orderedIds.length - 1])
      return
    }

    let activeId = ""
    for (const id of orderedIds) {
      if (this.visibleSections.has(id)) {
        activeId = id
        break
      }
    }

    this.setActive(activeId)
  }

  private setActive(activeId: string) {
    this.dotTargets.forEach((dot) => {
      const sectionId = dot.dataset.section || ""
      const isActive = sectionId === activeId
      dot.classList.toggle("reg-scrollspy__dot--active", isActive)
      dot.setAttribute("aria-current", isActive ? "true" : "false")

      const section = document.getElementById(sectionId)
      if (section) {
        section.classList.toggle("section--active", isActive)
      }
    })
  }
}
